import { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { C, PageHeader, GlassCard, Badge, Button, Skeleton } from "@coremonit/design-system";
import { ArrowLeft, Sparkles, ListTree, AlertTriangle, Zap } from "lucide-react";
import { useTrace, useTraceAISummary } from "../api/hooks";
import type { Span } from "@coremonit/types";

export function TraceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const trace = useTrace(id);
  const ai = useTraceAISummary();
  const [selected, setSelected] = useState<Span | null>(null);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader
        icon={<ListTree size={16} />}
        title={trace.data ? `Trace · ${trace.data.trace.rootOperation}` : "Trace"}
        subtitle={trace.data ? `${trace.data.trace.rootService} · ${trace.data.trace.durationMs}ms · ${trace.data.trace.spanCount} spans` : ""}
        actions={
          <>
            <Button variant="ghost" size="sm" icon={<ArrowLeft size={12} />} onClick={() => nav("/traces")}>Back to list</Button>
            <Button variant="glow" size="sm" icon={<Sparkles size={12} />} loading={ai.isPending} onClick={() => id && ai.mutate(id)}>AI summary</Button>
          </>
        }
      />

      {ai.data && (
        <GlassCard glow style={{ padding: 16 }} accent={C.borderBright}>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <div style={{ width: 30, height: 30, borderRadius: 10, background: `linear-gradient(135deg, ${C.blue}, ${C.cyan})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Sparkles size={14} color="#fff" />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ color: C.text, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>AI Trace Summary</div>
              <div style={{ color: C.textSub, fontSize: 12, lineHeight: 1.7 }}>{ai.data.summary}</div>
              <div style={{ marginTop: 10 }}>
                <div style={{ color: C.textMuted, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Critical path</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {ai.data.criticalPath.map((s, i) => <Badge key={i} color="blue">{s}</Badge>)}
                </div>
              </div>
              <div style={{ marginTop: 10 }}>
                <div style={{ color: C.textMuted, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Suggested actions</div>
                <ul style={{ margin: 0, paddingLeft: 20, color: C.textSub, fontSize: 12 }}>
                  {ai.data.suggestedActions.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            </div>
          </div>
        </GlassCard>
      )}

      {trace.isLoading ? <Skeleton height={480} /> : trace.data && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 12 }}>
          <GlassCard style={{ padding: 16 }}>
            <Waterfall spans={trace.data.spans} durationMs={trace.data.trace.durationMs} onSelect={setSelected} selectedId={selected?.id} />
          </GlassCard>
          <GlassCard style={{ padding: 16 }}>
            {selected ? <SpanDetail span={selected} /> : (
              <div style={{ color: C.textMuted, fontSize: 12, padding: 20, textAlign: "center" }}>Select a span to see attributes, events, and exceptions.</div>
            )}
          </GlassCard>
        </div>
      )}
    </div>
  );
}

function Waterfall({ spans, durationMs, onSelect, selectedId }: { spans: Span[]; durationMs: number; onSelect: (s: Span) => void; selectedId?: string }) {
  const t0 = new Date(spans[0]?.startedAt ?? Date.now()).getTime();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {spans.map((s) => {
        const start = new Date(s.startedAt).getTime() - t0;
        const leftPct = (start / durationMs) * 100;
        const widthPct = Math.max(0.5, (s.durationMs / durationMs) * 100);
        const color = s.status === "error" ? C.red : s.onCriticalPath ? C.blue : s.kind === "client" ? C.cyan : s.kind === "producer" ? C.purple : C.green;
        return (
          <button
            key={s.id}
            onClick={() => onSelect(s)}
            style={{
              display: "grid",
              gridTemplateColumns: "220px 1fr 60px",
              alignItems: "center",
              gap: 8,
              padding: "6px 8px",
              background: selectedId === s.id ? C.blueDim : "transparent",
              border: `1px solid ${selectedId === s.id ? C.borderBright : "transparent"}`,
              borderRadius: 8,
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ color: C.text, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.service}</div>
              <div style={{ color: C.textMuted, fontSize: 9, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.operation}</div>
            </div>
            <div style={{ position: "relative", height: 20 }}>
              <div style={{
                position: "absolute", top: 6, height: 8,
                left: `${leftPct}%`, width: `${widthPct}%`,
                background: color, borderRadius: 4,
                boxShadow: s.onCriticalPath ? `0 0 6px ${color}88` : "none",
              }} />
            </div>
            <div style={{ color: C.text, fontSize: 11, fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{s.durationMs}ms</div>
          </button>
        );
      })}
    </div>
  );
}

function SpanDetail({ span }: { span: Span }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: span.status === "error" ? C.red : C.blue }} />
        <div style={{ color: C.text, fontSize: 12, fontWeight: 700 }}>{span.service}.{span.operation}</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        <div><div style={{ color: C.textMuted, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>Kind</div><div style={{ color: C.text, fontSize: 11 }}>{span.kind}</div></div>
        <div><div style={{ color: C.textMuted, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>Duration</div><div style={{ color: C.text, fontSize: 11 }}>{span.durationMs}ms</div></div>
        <div><div style={{ color: C.textMuted, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>Status</div><div style={{ color: span.status === "error" ? C.red : C.green, fontSize: 11 }}>{span.status}</div></div>
        <div><div style={{ color: C.textMuted, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>Critical</div><div style={{ color: span.onCriticalPath ? C.blue : C.textMuted, fontSize: 11 }}>{span.onCriticalPath ? "yes" : "no"}</div></div>
      </div>
      <div style={{ color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>Attributes</div>
      <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: 8, fontSize: 10, fontFamily: "ui-monospace, monospace", color: C.textSub, marginBottom: 12 }}>
        {Object.entries(span.attributes).map(([k, v]) => (
          <div key={k}><span style={{ color: C.blue }}>{k}</span>: <span style={{ color: C.text }}>{String(v)}</span></div>
        ))}
      </div>
      {span.events.length > 0 && (
        <>
          <div style={{ color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>Events / Exceptions</div>
          {span.events.map((e, i) => (
            <div key={i} style={{ padding: 8, background: e.name === "exception" ? C.redDim : C.surfaceHigh, border: `1px solid ${e.name === "exception" ? "rgba(239,68,68,0.3)" : C.border}`, borderRadius: 8, marginBottom: 6, fontSize: 10 }}>
              <div style={{ color: e.name === "exception" ? C.red : C.text, fontWeight: 700 }}>{e.name}</div>
              <div style={{ color: C.textMuted, marginTop: 2 }}>{new Date(e.ts).toLocaleTimeString()}</div>
              {e.attributes && <div style={{ color: C.textSub, marginTop: 4, fontFamily: "ui-monospace, monospace" }}>{JSON.stringify(e.attributes)}</div>}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
